import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import {
  Form,
  SelectInput,
  TextInput,
  required,
  useDataProvider,
  useNotify,
  useRedirect,
} from 'react-admin';
import { useWatch } from 'react-hook-form';
import { RequireAccess } from '../auth/RequireAccess';
import type { AdminDataProvider } from '../dataProvider';
import {
  MACRO_ACTION_OPTIONS,
  TEMPLATE_VARIABLES,
} from './cannedResponsesState';

/**
 * Create a canned response template (D-canned-responses). A `response` is plain text; a
 * `macro` also bundles a ticket transition (resolve or escalate) the chokepoint guards.
 * New templates default to active. Creating one produces an `action='create'` audit entry
 * automatically through the provider wrapper; there is no audit code here.
 *
 * The route is gated `canned_responses:write` (super_admin only): a viewer, the editor and
 * the support agent are denied directly, not just missing the button.
 */

const KIND_CHOICES = [
  { id: 'response', name: 'Response' },
  { id: 'macro', name: 'Macro' },
];

const MACRO_CHOICES = MACRO_ACTION_OPTIONS.map((o) => ({
  id: o.value,
  name: o.label,
}));

interface CannedFormValues {
  name: string;
  body: string;
  kind: 'response' | 'macro';
  macroAction: string;
}

const VARIABLE_HINT = `Variables: ${TEMPLATE_VARIABLES.map((v) => `{${v}}`).join(' ')}`;

/** The macro-action select, shown only when the kind is `macro`. */
const MacroActionInput = () => {
  const kind = useWatch({ name: 'kind' });
  if (kind !== 'macro') return null;
  return (
    <SelectInput
      source="macroAction"
      label="Bundled action"
      choices={MACRO_CHOICES}
      validate={required('A macro needs a bundled action')}
    />
  );
};

const CannedResponseCreateForm = () => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();

  const onSubmit = async (raw: Record<string, unknown>) => {
    const values = raw as unknown as CannedFormValues;
    const action =
      values.kind === 'macro'
        ? (MACRO_ACTION_OPTIONS.find((o) => o.value === values.macroAction)
            ?.action ?? null)
        : null;
    try {
      const { data } = await dataProvider.create('cannedResponses', {
        data: {
          name: values.name,
          body: values.body,
          kind: values.kind,
          action,
          active: true,
          createdAt: new Date().toISOString(),
        },
      });
      notify('Template created', { type: 'success' });
      redirect('edit', 'cannedResponses', data.id);
    } catch {
      notify('Could not create the template', { type: 'error' });
    }
  };

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        New canned response
      </Typography>
      <Form
        defaultValues={{
          name: '',
          body: '',
          kind: 'response',
          macroAction: 'resolve',
        }}
        onSubmit={onSubmit}
      >
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <TextInput
              source="name"
              label="Name"
              validate={required('Name is required')}
              fullWidth
            />
            <SelectInput
              source="kind"
              label="Kind"
              choices={KIND_CHOICES}
              validate={required('Kind is required')}
            />
            <MacroActionInput />
            <TextInput
              source="body"
              label="Body"
              validate={required('Body is required')}
              helperText={VARIABLE_HINT}
              multiline
              minRows={3}
              fullWidth
            />
          </CardContent>
        </Card>
        <Stack direction="row" spacing={2}>
          <Button type="submit" variant="contained">
            Create
          </Button>
          <Button
            type="button"
            onClick={() => redirect('list', 'cannedResponses')}
          >
            Cancel
          </Button>
        </Stack>
      </Form>
    </Box>
  );
};

/** Gated entry point registered as the `cannedResponses` Resource's create. */
export const CannedResponseCreate = () => (
  <RequireAccess capability="canned_responses:write">
    <CannedResponseCreateForm />
  </RequireAccess>
);
